import type { AxiosError } from 'axios'
import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { TutorSlotLogPaginatedData } from '../../model/tutor-slot-log/interfaces'
import type { ApiCallbacks, ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { getFirstQueryValue, useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useTutorSlotLogStore } from '../../model/tutor-slot-log/store'

interface TutorSlotLogsApiOptions extends ApiCallbacks<TutorSlotLogPaginatedData> {
  route?: RouteLocationNormalizedGeneric
}

const emptyPaginatedData = (perPage: number): TutorSlotLogPaginatedData => ({
  logs: [],
  meta: {
    per_page: perPage,
    next_page_url: null,
    prev_page_url: null,
    current_page: 1,
    last_page: 1,
    total: 0,
  },
})

export const useTutorSlotLogsApi = async (
  options?: TutorSlotLogsApiOptions,
): Promise<TutorSlotLogPaginatedData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const route = options?.route || useRoute()
  const store = useTutorSlotLogStore()

  const tutorId = getFirstQueryValue(route.query?.tutor_id)
  const slotStart = getFirstQueryValue(route.query?.slot_start)

  // Both params are required by the backend FormRequest. Without them
  // Laravel validation fails and the response is a 302 redirect (HTML),
  // which the browser surfaces as "failed to load response data".
  if (!tutorId || !slotStart) return null

  const perPage = Number(getFirstQueryValue(route.query?.per_page)) || store.getPerPage

  const url = useAdminRoutes('admin.tutor-slots.logs', pick({
    tutor_id: tutorId,
    slot_start: slotStart,
    per_page: perPage,
    page: getFirstQueryValue(route.query?.page) || store.getCurrentPage,
  }))

  options?.onStart?.()
  store.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options?.onError?.(msg)
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      options?.onError?.(data.message)
      return null
    }

    const paginatedData: TutorSlotLogPaginatedData | null = data.data || null
    store.setLogsPaginatedData(paginatedData)
    options?.onSuccess?.(paginatedData)
    return paginatedData
  } catch (err: unknown) {
    // 404 means the (tutor_id, slot_start) pair didn't resolve to a real
    // tutor_slots row. Treat that as "no logs to show" instead of an error
    // toast — the user sees an empty table and can adjust their filters.
    const status = (err as AxiosError)?.response?.status
    if (status === 404) {
      const empty = emptyPaginatedData(perPage)
      store.setLogsPaginatedData(empty)
      options?.onSuccess?.(empty)
      return empty
    }

    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options?.onError?.(msg)
    return null
  } finally {
    // Always flip the "search attempt complete" flag — otherwise the page
    // watcher (`isSearchable && !hasSearched && !isLoading`) refires after
    // any failure path that returns null, creating a toast-spamming retry
    // loop.
    store.setHasSearched(true)
    store.setLoading(false)
    options?.onFinish?.()
  }
}
